import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, ArrowRight, MessageCircle, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
    PHONE_DISPLAY,
    PHONE_HREF,
    WHATSAPP_HREF,
    NAV_LINKS,
    LOGO_SRC,
} from "@/lib/constants";
import { track } from "@/lib/track";

const SCROLL_THRESHOLD = 50;

/**
 * Text-shadow used in the transparent (at-top) state to keep white text
 * legible over the dark gradient veil.
 */
const WHITE_TEXT_SHADOW = { textShadow: "0 1px 3px rgba(0,0,0,0.45)" };

/**
 * Desktop nav link.
 *  - At-top   : white text + drop shadow, hover → red, active dot → white.
 *  - Scrolled : near-black text (no shadow), hover → red, active dot → red.
 */
const DesktopNavLink = ({ to, label, scrolled }) => (
    <NavLink
        to={to}
        end={to === "/"}
        data-testid={`nav-link-${to.replace(/[^a-z0-9]+/gi, "-")}`}
    >
        {({ isActive }) => (
            <span
                style={scrolled ? undefined : WHITE_TEXT_SHADOW}
                className={`relative inline-flex flex-col items-center text-sm font-semibold tracking-wide transition-colors duration-300 group ${
                    scrolled
                        ? isActive
                            ? "text-brand-red"
                            : "text-[#1F1F1F] hover:text-brand-red"
                        : isActive
                          ? "text-white"
                          : "text-white/95 hover:text-brand-red"
                }`}
            >
                {label}
                {/* Active dot indicator — white on top, red when scrolled */}
                <span
                    aria-hidden="true"
                    className={`absolute -bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                        scrolled ? "bg-brand-red" : "bg-white"
                    } ${
                        isActive
                            ? "opacity-100 scale-100"
                            : "opacity-0 scale-0 group-hover:bg-brand-red group-hover:opacity-80 group-hover:scale-100"
                    }`}
                />
            </span>
        )}
    </NavLink>
);

const Hamburger = ({ open, onClick, scrolled }) => (
    <button
        type="button"
        data-testid="mobile-menu-toggle"
        onClick={onClick}
        aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
        aria-expanded={open}
        style={scrolled ? undefined : { filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.35))" }}
        className={`lg:hidden inline-flex flex-col justify-center items-center w-11 h-11 rounded-lg transition-colors duration-300 ${
            scrolled
                ? "text-[#1F1F1F] hover:bg-black/5 hover:text-brand-red"
                : "text-white hover:bg-white/10 hover:text-brand-red"
        }`}
    >
        <motion.span
            animate={open ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.25 }}
            className="block w-6 h-[2px] bg-current rounded-full"
        />
        <motion.span
            animate={open ? { opacity: 0 } : { opacity: 1 }}
            transition={{ duration: 0.15 }}
            className="block w-6 h-[2px] bg-current rounded-full my-[5px]"
        />
        <motion.span
            animate={open ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.25 }}
            className="block w-6 h-[2px] bg-current rounded-full"
        />
    </button>
);

const Header = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(
        () => typeof window !== "undefined" && window.scrollY > SCROLL_THRESHOLD,
    );

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileOpen]);

    // Header chrome — two states, smooth transition.
    const headerSurfaceClasses = scrolled
        ? "bg-white shadow-sm"
        : "bg-transparent";

    return (
        <>
            <header
                data-testid="site-header"
                data-header-mode={scrolled ? "scrolled" : "top"}
                className={`sticky top-0 z-40 transition-[background-color,box-shadow] duration-300 ease-out ${headerSurfaceClasses}`}
            >
                {/* Dark gradient veil — faded out when scrolled */}
                <div
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-x-0 top-0 h-[140px] transition-opacity duration-300 ${
                        scrolled ? "opacity-0" : "opacity-100"
                    }`}
                    style={{
                        background:
                            "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.30) 45%, rgba(0,0,0,0) 100%)",
                    }}
                />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-[72px] md:h-[88px]">
                        {/* Logo — no pill in either state */}
                        <Link
                            to="/"
                            data-testid="header-logo-link"
                            className="flex items-center shrink-0 transition-[filter] duration-300"
                            aria-label="Capital Automobile - Accueil"
                            style={
                                scrolled
                                    ? undefined
                                    : { filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.35))" }
                            }
                        >
                            <img
                                src={LOGO_SRC}
                                alt="Capital Automobile"
                                className="h-[52px] md:h-16 w-auto"
                            />
                        </Link>

                        {/* Desktop nav */}
                        <nav
                            className="hidden lg:flex items-center gap-9"
                            aria-label="Navigation principale"
                        >
                            {NAV_LINKS.map((link) => (
                                <DesktopNavLink
                                    key={link.to}
                                    to={link.to}
                                    label={link.label}
                                    scrolled={scrolled}
                                />
                            ))}
                        </nav>

                        {/* Right actions */}
                        <div className="flex items-center gap-3 md:gap-4">
                            {/* Phone */}
                            <a
                                href={PHONE_HREF}
                                data-testid="header-phone-link"
                                onClick={() => track("phone_click", { location: "header" })}
                                style={scrolled ? undefined : WHITE_TEXT_SHADOW}
                                className={`hidden md:inline-flex items-center gap-2.5 text-sm font-semibold transition-colors duration-300 group ${
                                    scrolled
                                        ? "text-[#1F1F1F] hover:text-brand-red"
                                        : "text-white hover:text-brand-red"
                                }`}
                            >
                                <span
                                    className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors duration-300 ${
                                        scrolled
                                            ? "bg-brand-red/10 group-hover:bg-brand-red text-brand-red group-hover:text-white"
                                            : "bg-white/15 group-hover:bg-brand-red text-white"
                                    }`}
                                >
                                    <Phone className="w-4 h-4" />
                                </span>
                                <span className="flex flex-col leading-tight">
                                    <span className="tabular-nums tracking-wide">
                                        {PHONE_DISPLAY}
                                    </span>
                                    <span
                                        className={`text-[10px] font-medium uppercase tracking-wider transition-colors duration-300 ${
                                            scrolled
                                                ? "text-[#1F1F1F]/55"
                                                : "text-white/85"
                                        }`}
                                    >
                                        7/7 — 7h-22h
                                    </span>
                                </span>
                            </a>

                            {/* CTA — red pill (unchanged in both states) */}
                            <Link
                                to="/demande-offre"
                                data-testid="header-cta-button"
                                onClick={() =>
                                    track("cta_request_offer_click", { location: "header" })
                                }
                                className="relative overflow-hidden hidden md:inline-flex items-center gap-2 bg-brand-red text-white pl-5 pr-4 py-2.5 rounded-full font-semibold text-sm hover:bg-brand-red-hover hover:scale-[1.03] hover:shadow-lg transition-all duration-200 red-cta-shadow group"
                            >
                                {/* Shimmer */}
                                <span
                                    aria-hidden="true"
                                    className="absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-white/35 to-transparent skew-x-12 group-hover:left-full transition-[left] duration-700 ease-out"
                                />
                                <span className="relative inline-flex items-center gap-2">
                                    Demander une offre
                                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/15 group-hover:translate-x-0.5 transition-transform duration-200">
                                        <ArrowRight className="w-3 h-3" />
                                    </span>
                                </span>
                            </Link>

                            <Hamburger
                                open={mobileOpen}
                                onClick={() => setMobileOpen((v) => !v)}
                                scrolled={scrolled}
                            />
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile slide-in panel + backdrop */}
            <AnimatePresence>
                {mobileOpen ? (
                    <>
                        <motion.div
                            key="backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={() => setMobileOpen(false)}
                            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[1px] lg:hidden"
                            aria-hidden="true"
                            data-testid="mobile-menu-backdrop"
                        />
                        <motion.aside
                            key="panel"
                            data-testid="mobile-menu-panel"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
                            className="fixed top-0 right-0 bottom-0 w-[85%] max-w-md bg-white z-50 flex flex-col shadow-2xl lg:hidden"
                            aria-label="Menu de navigation"
                        >
                            {/* Top: logo + close */}
                            <div className="flex items-center justify-between h-[72px] px-4 border-b border-gray-100">
                                <Link to="/" onClick={() => setMobileOpen(false)}>
                                    <img
                                        src={LOGO_SRC}
                                        alt="Capital Automobile"
                                        className="h-[52px] w-auto"
                                    />
                                </Link>
                                <button
                                    type="button"
                                    data-testid="mobile-menu-close"
                                    onClick={() => setMobileOpen(false)}
                                    className="inline-flex items-center justify-center w-11 h-11 rounded-lg text-brand-dark hover:bg-gray-100"
                                    aria-label="Fermer le menu"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Links — stagger */}
                            <nav className="flex-1 px-4 py-6 overflow-y-auto">
                                {NAV_LINKS.map((link, idx) => (
                                    <motion.div
                                        key={link.to}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.05 + idx * 0.06, duration: 0.25 }}
                                    >
                                        <NavLink
                                            to={link.to}
                                            end={link.to === "/"}
                                            data-testid={`mobile-nav-link-${link.to.replace(/[^a-z0-9]+/gi, "-")}`}
                                            onClick={() => setMobileOpen(false)}
                                        >
                                            {({ isActive }) => (
                                                <span
                                                    className={`flex items-center justify-between px-4 py-3.5 rounded-xl text-xl font-bold transition-colors ${
                                                        isActive
                                                            ? "text-brand-red bg-brand-red/5"
                                                            : "text-brand-dark hover:bg-gray-50"
                                                    }`}
                                                >
                                                    <span>{link.label}</span>
                                                    {isActive ? (
                                                        <span
                                                            aria-hidden="true"
                                                            className="w-2 h-2 rounded-full bg-brand-red"
                                                        />
                                                    ) : null}
                                                </span>
                                            )}
                                        </NavLink>
                                    </motion.div>
                                ))}
                            </nav>

                            {/* Bottom: CTA + tel + WhatsApp */}
                            <div className="p-4 border-t border-gray-100 space-y-3">
                                <Link
                                    to="/demande-offre"
                                    data-testid="mobile-menu-cta-button"
                                    onClick={() => {
                                        track("cta_request_offer_click", { location: "mobile_menu" });
                                        setMobileOpen(false);
                                    }}
                                    className="flex items-center justify-center gap-2 w-full bg-brand-red text-white py-4 rounded-full font-bold text-base hover:bg-brand-red-hover transition-colors red-cta-shadow"
                                >
                                    Demander une offre
                                    <ArrowRight className="w-5 h-5" />
                                </Link>
                                <a
                                    href={PHONE_HREF}
                                    data-testid="mobile-menu-phone-link"
                                    onClick={() => track("phone_click", { location: "mobile_menu" })}
                                    className="flex items-center justify-center gap-2 w-full border-2 border-brand-red text-brand-red py-3 rounded-full font-semibold text-sm"
                                >
                                    <Phone className="w-4 h-4" />
                                    {PHONE_DISPLAY}
                                </a>
                                <a
                                    href={WHATSAPP_HREF}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="mobile-menu-whatsapp-link"
                                    onClick={() =>
                                        track("whatsapp_click", { location: "mobile_menu" })
                                    }
                                    className="flex items-center justify-center gap-2 w-full bg-whatsapp text-white py-3 rounded-full font-semibold text-sm hover:bg-whatsapp-hover transition-colors"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    WhatsApp
                                </a>
                            </div>
                        </motion.aside>
                    </>
                ) : null}
            </AnimatePresence>
        </>
    );
};

export default Header;
